import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { teamPhotoService } from '@/services/teamPhotoService';
import { supabase } from '@/integrations/supabase/client';
import { Camera, Upload, X, Image } from 'lucide-react';
import { toast } from 'sonner';

interface TeamPhotoUploadFormProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  onUpdate: () => void;
}

export const TeamPhotoUploadForm = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  onUpdate
}: TeamPhotoUploadFormProps) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file types
    const invalidFiles = files.filter(file => !file.type.match(/^image\/(jpeg|jpg|png)$/));
    if (invalidFiles.length > 0) {
      toast.error('Please upload only JPEG or PNG images');
      return;
    }

    // Validate file sizes (max 5MB each)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error('Each image must be less than 5MB');
      return;
    }

    setSelectedFiles(files);

    // Create previews
    const newPreviews: string[] = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        if (newPreviews.length === files.length) {
          setPreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadPhotos = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select photos to upload');
      return;
    }

    try {
      setUploading(true);
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const sanitizedTeamName = teamName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
        const fileName = `team-photo-${sanitizedTeamName}-${Date.now()}-${i}`;
        
        // Upload the file using teamPhotoService
        const result = await teamPhotoService.uploadTeamPhoto(file, fileName);
        
        if (!result.success) {
          toast.error(result.error || `Failed to upload photo ${i + 1}`);
          continue;
        }

        // Insert record into team_photos table
        if (result.url) {
          // Get current user ID for uploaded_by field - prioritize internal user
          let currentUserId: string | null = null;
          
          // First check internal_user_session which is where useInternalAuth stores the user
          try {
            const sessionData = localStorage.getItem('internal_user_session');
            if (sessionData) {
              console.log('Found internal_user_session:', sessionData);
              const parsed = JSON.parse(sessionData);
              if (parsed && parsed.user && parsed.user.id) {
                currentUserId = parsed.user.id;
                console.log('Using internal user ID from session for photo upload:', currentUserId);
              }
            }
          } catch (e) {
            console.log('Error parsing internal_user_session:', e);
          }
          
          // If no internal user session, try Supabase auth
          if (!currentUserId) {
            const { data: { user } } = await supabase.auth.getUser();
            currentUserId = user?.id || null;
            if (currentUserId) {
              console.log('Using Supabase user ID for team photo upload:', currentUserId);
            }
          }

          if (currentUserId) {
            const { error: dbError } = await supabase
              .from('team_photos')
              .insert({
                team_id: teamId,
                photo_url: result.url,
                uploaded_by: currentUserId
              });

            if (dbError) {
              console.error('Database error:', dbError);
              toast.error(`Failed to save photo ${i + 1} to database: ${dbError.message}`);
            } else {
              console.log(`Successfully saved photo ${i + 1} to database`);
            }
          } else {
            console.error('No user ID found for photo upload');
            toast.error(`Failed to save photo ${i + 1}: User not authenticated`);
          }
        }
      }

      toast.success(`Successfully uploaded ${selectedFiles.length} photo(s)`);
      onUpdate();
      clearForm();
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const clearForm = () => {
    setSelectedFiles([]);
    setPreviews([]);
  };

  const removePhoto = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Image className="h-5 w-5" />
            Upload Team Photos - {teamName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Photos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="photo-upload">Choose Photos</Label>
                <Input
                  id="photo-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  multiple
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="mt-2"
                />
              </div>

              {selectedFiles.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Selected: {selectedFiles.length} photo(s)
                </div>
              )}

              {/* Photo Previews */}
              {previews.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {previews.map((preview, index) => (
                    <div key={index} className="relative">
                      <img 
                        src={preview} 
                        alt={`Preview ${index + 1}`} 
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={uploadPhotos}
                  disabled={selectedFiles.length === 0 || uploading}
                  className="flex-1"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Photo(s)`}
                </Button>
                
                {selectedFiles.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearForm}
                    disabled={uploading}
                  >
                    Clear
                  </Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground">
                Upload JPEG or PNG format. Max size 5MB per photo. Multiple photos allowed.
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};